import { ConsentSnapshot } from '../types'
import { applyGrant, applyDeny } from './consentMode'

const CONSENT_KEY = 'cookie_consent'

type Subscriber = (s: ConsentSnapshot) => void

const DENIED_SNAPSHOT: ConsentSnapshot = {
  ad_storage: 'denied',
  analytics_storage: 'denied',
  ad_user_data: 'denied',
  ad_personalization: 'denied',
}

const GRANTED_SNAPSHOT: ConsentSnapshot = {
  ad_storage: 'granted',
  analytics_storage: 'granted',
  ad_user_data: 'granted',
  ad_personalization: 'granted',
}

function readStoredValue(): string | null {
  try {
    return localStorage.getItem(CONSENT_KEY)
  } catch {
    return null
  }
}

function writeStoredValue(value: string): void {
  try {
    localStorage.setItem(CONSENT_KEY, value)
  } catch {
    // localStorage unavailable
  }
}

function initSnapshot(): ConsentSnapshot {
  const raw = readStoredValue()
  if (raw === 'granted') return { ...GRANTED_SNAPSHOT }
  return { ...DENIED_SNAPSHOT }
}

export class ConsentStore {
  private state: ConsentSnapshot = initSnapshot()
  private readonly subscribers: Set<Subscriber> = new Set()

  snapshot(): ConsentSnapshot {
    return { ...this.state }
  }

  isGranted(): boolean {
    return this.state.ad_storage === 'granted'
  }

  /** Returns true if the user has explicitly chosen granted or denied (not pending). */
  isDecided(): boolean {
    const raw = readStoredValue()
    return raw === 'granted' || raw === 'denied'
  }

  grant(): void {
    this.state = { ...GRANTED_SNAPSHOT }
    writeStoredValue('granted')
    this.notify()
    applyGrant()
  }

  deny(): void {
    this.state = { ...DENIED_SNAPSHOT }
    writeStoredValue('denied')
    this.notify()
    applyDeny()
  }

  subscribe(fn: Subscriber): () => void {
    this.subscribers.add(fn)
    return () => {
      this.subscribers.delete(fn)
    }
  }

  private notify(): void {
    const snap = this.snapshot()
    this.subscribers.forEach(fn => fn(snap))
  }
}

export const consentStore = new ConsentStore()
